import { FC, useEffect, useState } from "react";
import {
  CompanyInvoices,
  SuccinctInvoice,
} from "@vviinn/user-managment-client-ts";
import { FileDown } from "lucide-react";

import { DynamicFormCardWrapperProps } from "./components/DynamicFormCardWrapper";
import { Button } from "../../../ui/layout/molecules/Button";
import { getCompanyInvoices } from "../../../store";
import { downloadFile } from "../../../helpers";
import { startEndDate } from "./components/startEndDate";
import { CompanyPricingItem } from "./components/CompanyPricingItem";
import { useRequestHandlers } from "../../../app/hooks/useRequestHandlers";

export const invoicePeriod = (startDate: Date) =>
  parseInt(startDate.toString().slice(2, 8).replace("-", ""));

type InvoicesProps = { companyId: string };

const initialInvoices: CompanyInvoices = {
  items: { all: [], last3: [] },
  warning: undefined,
};

export const Invoices: FC<InvoicesProps> = ({ companyId }) => {
  const [invoices, setInvoices] = useState<CompanyInvoices>(initialInvoices);
  const { getApi, dispatch } = useRequestHandlers();

  useEffect(() => {
    setInvoices(initialInvoices);
    const loadInvoices = async () => {
      const api = await getApi();
      const result = await dispatch(
        getCompanyInvoices({ api, value: companyId }),
      );
      if (result.payload) setInvoices(result.payload as CompanyInvoices);
    };
    loadInvoices();
  }, [companyId]);

  const downloadCompanyInvoice = async (period?: number) => {
    if (!period) return;
    const api = await getApi();
    const response = await api.downloadCompanyInvoice(companyId, period);
    if (!response) {
      return;
    }
    downloadFile(
      response as Blob,
      `company_${companyId}_invoice_${period}.pdf`,
    );
  };

  const invoiceDataToCard = ({
    id,
    startDate,
    endDate,
    totalPrice,
  }: SuccinctInvoice): DynamicFormCardWrapperProps => ({
    enabled: true,
    title: `Invoice ${id}`,
    subtitles: [
      {
        type: "text",
        text: startEndDate(startDate, endDate),
      },
    ],
    textAtRight: `${totalPrice.toFixed(2)} €`,
    buttonAtRight: (
      <Button
        title="Download invoice"
        buttonType="iconOnly"
        onClick={() => {
          downloadCompanyInvoice(invoicePeriod(startDate));
        }}
      >
        <FileDown className="h-8 w-8 justify-self-end text-stone-500 underline hover:text-stone-600" />
      </Button>
    ),
  });

  return (
    <CompanyPricingItem
      type="invoices"
      cardsData={invoices.items.all.map(invoiceDataToCard)}
      warning={invoices.warning}
    />
  );
};
